import { z } from 'zod';
import {
  CounterpartySelectionSchema,
  CounterpartySnapshotSchema,
} from '../counterparties/counterparties.schema.js';
import {
  ShipmentBuilderIntentSchema,
  ShipmentSourceEvidenceSchema,
  ShipmentTotalsSummarySchema,
  validateShipmentSourceEvidencePair,
} from '../shipments/shipments-shared.schema.js';
import { IsoDateTimeSchema } from '../shipments/shipments-types.js';
import { ReportableWeightLbSchema } from '../reportable-weight.schema.js';
import type {
  CoverShipmentAttachmentItem,
  CoverShipmentAttachmentSelection,
  CoverShipmentDetail,
  CoverShipmentHeader,
} from './cover-shipments-shared.dto.js';
import { CoverShipmentStatusSchema } from './cover-shipments-types.js';

const trimmedStringSchema = z.string().trim().min(1);

export const CoverShipmentAttachmentSelectionSchema: z.ZodType<CoverShipmentAttachmentSelection> = z
  .object({
    receiptId: z.uuid(),
  })
  .strict();

export const CoverShipmentAttachmentItemSchema: z.ZodType<CoverShipmentAttachmentItem> = z
  .object({
    receiptId: z.uuid(),
    materialId: z.uuid(),
    receivedWeightLb: ReportableWeightLbSchema,
    coverContributionWeightLb: ReportableWeightLbSchema,
    coverContributionRefundValueCents: z.number().int().min(0),
    receivedAt: IsoDateTimeSchema,
    shippingReportNumber: trimmedStringSchema,
    shipper: CounterpartySnapshotSchema,
  })
  .strict();

export const CoverShipmentHeaderSchema: z.ZodType<CoverShipmentHeader> = z
  .object({
    coverShipmentId: z.uuid(),
    companyId: z.uuid(),
    stationId: z.uuid(),
    status: CoverShipmentStatusSchema,
    sourcePreparationRequestId: z.uuid().nullable().optional(),
    materialId: z.uuid(),
    shipper: CounterpartySnapshotSchema,
    receiver: CounterpartySnapshotSchema,
    builderIntent: ShipmentBuilderIntentSchema,
    attachmentCount: z.number().int().min(0),
    attachmentsWeightLb: ReportableWeightLbSchema,
    ownPurchaseWeightLb: ReportableWeightLbSchema,
    totalWeightLb: ReportableWeightLbSchema,
    createdAt: IsoDateTimeSchema,
    departedAt: IsoDateTimeSchema.nullable().optional(),
    voidedAt: IsoDateTimeSchema.nullable(),
    ...ShipmentSourceEvidenceSchema.shape,
  })
  .superRefine(validateShipmentSourceEvidencePair)
  .strict();

export const CoverShipmentDetailSchema: z.ZodType<CoverShipmentDetail> = z
  .object({
    coverShipmentId: z.uuid(),
    companyId: z.uuid(),
    stationId: z.uuid(),
    status: CoverShipmentStatusSchema,
    sourcePreparationRequestId: z.uuid().nullable().optional(),
    materialId: z.uuid(),
    shipper: CounterpartySnapshotSchema,
    receiver: CounterpartySnapshotSchema,
    builderIntent: ShipmentBuilderIntentSchema,
    attachmentCount: z.number().int().min(0),
    attachmentsWeightLb: ReportableWeightLbSchema,
    ownPurchaseWeightLb: ReportableWeightLbSchema,
    totalWeightLb: ReportableWeightLbSchema,
    createdAt: IsoDateTimeSchema,
    departedAt: IsoDateTimeSchema.nullable().optional(),
    voidedAt: IsoDateTimeSchema.nullable(),
    ...ShipmentSourceEvidenceSchema.shape,
    attachments: z.array(CoverShipmentAttachmentItemSchema),
    ownPurchaseLineItemIds: z.array(z.uuid()),
    purchaseCommittedIds: z.array(z.uuid()),
    totals: ShipmentTotalsSummarySchema,
  })
  .superRefine(validateShipmentSourceEvidencePair)
  .strict();

export const CoverShipmentReceiverSelectionSchema: z.ZodType<import('./cover-shipments-shared.dto.js').CoverShipmentReceiverSelection> =
  CounterpartySelectionSchema;
